// ThemeToggle — 3-state cycle button for dark mode.
//
// Cycle: system → dark → light → system.
// Placed in course-sidebar footer AND global AppHeader.
// Session 42: Dark mode foundation.
// Session 43: variant prop for header vs sidebar styling.

import { Sun, Moon, Monitor } from 'lucide-react';
import { useStore } from '../lib/store/index.js';
import { useT } from '../i18n/index.js';
import { nextThemeMode } from '../lib/hooks/use-theme-sync.js';
import type { ThemeMode } from '../lib/store/ui-slice.js';

type Variant = 'sidebar' | 'header';

const ICONS: Record<ThemeMode, typeof Sun> = {
  system: Monitor,
  dark: Moon,
  light: Sun,
};

const CLASS_BY_VARIANT: Record<Variant, string> = {
  sidebar: 'course-sidebar-footer-icon theme-toggle-btn',
  header: 'header-theme-btn',
};

const SIZE_BY_VARIANT: Record<Variant, number> = {
  sidebar: 14,
  header: 16,
};

interface ThemeToggleProps {
  /** Where the toggle lives — controls CSS class. Default: 'sidebar'. */
  variant?: Variant;
}

export function ThemeToggle({ variant = 'sidebar' }: ThemeToggleProps) {
  const theme = useStore((s) => s['theme']);
  const setTheme = useStore((s) => s['setTheme']);
  const t = useT();

  const Icon = ICONS[theme];
  const label = t(`theme.${theme}`);

  return (
    <button
      type="button"
      className={CLASS_BY_VARIANT[variant]}
      title={label}
      aria-label={label}
      onClick={() => setTheme(nextThemeMode(theme))}
    >
      <Icon size={SIZE_BY_VARIANT[variant]} />
    </button>
  );
}
